import { Component, Input, OnChanges, forwardRef } from '@angular/core'
import { NgFor } from '@angular/common'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'

export interface SearchSelectOption {
  value: string
  label: string
  /** Extra text to match on, e.g. a currency's full name. */
  search?: string
}

/** A select with a search box; works with formControlName like a native <select>. */
@Component({
  selector: 'app-search-select',
  standalone: true,
  imports: [NgFor, FormsModule, MatAutocompleteModule],
  templateUrl: './search-select.component.html',
  styleUrl: './search-select.component.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchSelectComponent), multi: true }]
})
export class SearchSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() options: SearchSelectOption[] = []
  @Input() placeholder = ''

  query = ''
  disabled = false
  private value = ''
  private onChange: (value: string) => void = () => {}
  private onTouched: () => void = () => {}

  ngOnChanges(): void {
    this.query = this.labelOf(this.value)
  }

  /** All options until the merchant types something other than the current label. */
  get filtered(): SearchSelectOption[] {
    const q = this.query.trim().toLowerCase()
    if (!q || q === this.labelOf(this.value).toLowerCase()) return this.options
    return this.options.filter((o) => `${o.label} ${o.search ?? ''}`.toLowerCase().includes(q))
  }

  labelOf = (value: string): string => this.options.find((o) => o.value === value)?.label ?? ''

  select(value: string): void {
    this.value = value
    this.query = this.labelOf(value)
    this.onChange(value)
  }

  /**
   * Puts the selected label back if the merchant typed without picking an option.
   * Runs when the list closes, not on blur: pressing an option blurs the input first,
   * and resetting then would reshuffle the list under the click.
   */
  restoreLabel(): void {
    this.query = this.labelOf(this.value)
  }

  onBlur(): void {
    this.onTouched()
  }

  writeValue(value: string): void {
    this.value = value ?? ''
    this.query = this.labelOf(this.value)
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled
  }
}
